import {
  memo, useEffect, useState,
} from '../../lib/teact/teact';

import type { Operator } from '../../services/crmStorage';

import { CRM_BACKEND_URL as BACKEND_URL } from '../../util/crmBackendUrl';
import {
  fetchOperatorsApi, getActiveOperator, isSuperUser, OPERATORS,
} from '../../services/crmStorage';

import useLastCallback from '../../hooks/useLastCallback';

import Icon from '../common/icons/Icon';
import AiConfigModal, { type AiConfigData } from './AiConfigModal';
import HelixConfigModal, { type HelixConfigData } from './HelixConfigModal';
import OperatorsModal from './OperatorsModal';
import PromptEditorModal, { type PromptKey, type PromptsConfig } from './PromptEditorModal';
import ScheduleEditorModal, { type BusinessHoursData } from './ScheduleEditorModal';
import TelethonAuthModal from './TelethonAuthModal';

import styles from './SystemSettingsView.module.scss';

const OPERATORS_STORAGE_KEY = 'telegram_crm_operators_v1';

export interface TelethonStatus {
  status: string;
  is_connected: boolean;
  is_authorized: boolean;
  user?: {
    id: number;
    username?: string;
    first_name?: string;
    phone?: string;
  };
  configured_phone?: string;
  uptime_seconds?: number;
  session_storage?: string;
  storage_details?: string;
  db_persisted?: boolean;
  dc_id?: number;
  has_db_session?: boolean;
}

function loadOperators(): Operator[] {
  try {
    const raw = localStorage.getItem(OPERATORS_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Operator[];
      if (Array.isArray(parsed) && parsed.length > 0) return parsed;
    }
  } catch {
    // Fallback al array estático
  }
  return OPERATORS;
}

function formatWorkDays(days?: Record<string, { active: boolean; start: string; end: string }>) {
  if (!days) return 'Lunes a Viernes';
  const dayNames: Record<string, string> = {
    monday: 'Lun',
    tuesday: 'Mar',
    wednesday: 'Mié',
    thursday: 'Jue',
    friday: 'Vie',
    saturday: 'Sáb',
    sunday: 'Dom',
  };
  const activeKeys = Object.keys(days).filter((k) => days[k]?.active);
  if (activeKeys.length === 5 && !days.saturday?.active && !days.sunday?.active) {
    return 'Lunes a Viernes';
  }
  if (activeKeys.length === 6 && days.saturday?.active && !days.sunday?.active) {
    return 'Lunes a Sábado';
  }
  if (activeKeys.length === 7) {
    return 'Lunes a Domingo';
  }
  return activeKeys.map((k) => dayNames[k] || k).join(', ');
}

function formatWorkHours(days?: Record<string, { active: boolean; start: string; end: string }>) {
  if (!days) return '08:00 a 16:00 (Continuo)';
  const mon = days.monday;
  if (mon && mon.active) {
    return `${mon.start} a ${mon.end} (Continuo)`;
  }
  const firstActive = Object.values(days).find((d) => d.active);
  if (firstActive) {
    return `${firstActive.start} a ${firstActive.end} (Continuo)`;
  }
  return 'Personalizado';
}

const SystemSettingsView = () => {
  const [institutionalPrompt, setInstitutionalPrompt] = useState('');
  const [ragPrompt, setRagPrompt] = useState('');
  const [formattingPrompt, setFormattingPrompt] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Business Hours & AI Config State
  const [businessHours, setBusinessHours] = useState<BusinessHoursData | undefined>(undefined);
  const [aiConfig, setAiConfig] = useState<AiConfigData | undefined>(undefined);
  const [telethonStatus, setTelethonStatus] = useState<TelethonStatus | undefined>(undefined);
  const [isTelethonChecking, setIsTelethonChecking] = useState(true);
  const [helixConfig, setHelixConfig] = useState<HelixConfigData | undefined>(undefined);

  // Operators state
  const [operators, setOperators] = useState<Operator[]>(loadOperators);
  const [currentOperator, setCurrentOperator] = useState<Operator>(getActiveOperator);
  const canManageTeam = isSuperUser(currentOperator);

  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [isAiModalOpen, setIsAiModalOpen] = useState(false);
  const [isTelethonModalOpen, setIsTelethonModalOpen] = useState(false);
  const [isHelixModalOpen, setIsHelixModalOpen] = useState(false);
  const [isPromptModalOpen, setIsPromptModalOpen] = useState(false);
  const [isOperatorsModalOpen, setIsOperatorsModalOpen] = useState(false);
  const [activePromptKey, setActivePromptKey] = useState<PromptKey>('institutional');
  const [isSavingSchedule, setIsSavingSchedule] = useState(false);
  const [isSavingAi, setIsSavingAi] = useState(false);
  const [isSavingHelix, setIsSavingHelix] = useState(false);

  useEffect(() => {
    const handleOperatorChanged = () => {
      setCurrentOperator(getActiveOperator());
    };

    window.addEventListener('crm:operator-changed', handleOperatorChanged);
    window.addEventListener('crm:operators-changed', handleOperatorChanged);
    return () => {
      window.removeEventListener('crm:operator-changed', handleOperatorChanged);
      window.removeEventListener('crm:operators-changed', handleOperatorChanged);
    };
  }, []);

  const handleOpenPromptModal = useLastCallback((key: PromptKey) => {
    setActivePromptKey(key);
    setIsPromptModalOpen(true);
  });

  const handleOpenOperatorsModal = useLastCallback(() => {
    if (!canManageTeam) return;
    setIsOperatorsModalOpen(true);
  });

  const handleOperatorsChange = useLastCallback((updated: Operator[]) => {
    setOperators(updated);
  });

  const loadData = useLastCallback(async () => {
    setIsLoading(true);
    setErrorMessage('');
    try {
      // Load Prompts
      const promptsRes = await fetch(`${BACKEND_URL}/ai-configs/prompts`);
      if (promptsRes.ok) {
        const data = await promptsRes.json();
        setInstitutionalPrompt(data.institutional_prompt || '');
        setRagPrompt(data.rag_context_prompt || '');
        setFormattingPrompt(data.formatting_prompt || '');
      }

      // Load Business Hours
      const hoursRes = await fetch(`${BACKEND_URL}/business-hours`);
      if (hoursRes.ok) {
        const hoursData = await hoursRes.json();
        setBusinessHours(hoursData);
      }

      // Load AI Config
      const aiRes = await fetch(`${BACKEND_URL}/ai-configs`);
      if (aiRes.ok) {
        const aiData = await aiRes.json();
        setAiConfig(aiData);
      }

      // Load Telethon 24/7 Daemon Status
      setIsTelethonChecking(true);
      try {
        const telethonRes = await fetch(`${BACKEND_URL}/telethon/status`);
        if (telethonRes.ok) {
          const telethonData = await telethonRes.json();
          setTelethonStatus(telethonData);
        }
      } catch {
        // Daemon offline o no disponible
      } finally {
        setIsTelethonChecking(false);
      }

      // Load Operators from MariaDB
      try {
        const remoteOps = await fetchOperatorsApi();
        if (remoteOps && remoteOps.length > 0) {
          setOperators(remoteOps);
        }
      } catch {
        // Fallback a operadores existentes
      }

      // Load HELIX Config
      try {
        const helixRes = await fetch(`${BACKEND_URL}/helix/config`);
        if (helixRes.ok) {
          const helixData = await helixRes.json();
          setHelixConfig(helixData);
        }
      } catch {
        // HELIX endpoint offline o no disponible
      }
    } catch {
      setErrorMessage('Error de conexión al cargar la configuración del sistema.');
    } finally {
      setIsLoading(false);
    }
  });

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleResetDefaults = useLastCallback(async () => {
    if (!window.confirm('¿Estás seguro de restablecer los 3 prompts a los valores predeterminados de fábrica?')) {
      return;
    }
    setIsSaving(true);
    setSaveSuccess(false);
    setErrorMessage('');
    try {
      const res = await fetch(`${BACKEND_URL}/ai-configs/prompts/reset`, {
        method: 'POST',
      });
      if (res.ok) {
        const data = await res.json();
        setInstitutionalPrompt(data.institutional_prompt || '');
        setRagPrompt(data.rag_context_prompt || '');
        setFormattingPrompt(data.formatting_prompt || '');
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 4000);
      } else {
        setErrorMessage('No se pudieron restablecer los valores por defecto.');
      }
    } catch {
      setErrorMessage('Error al restablecer los prompts.');
    } finally {
      setIsSaving(false);
    }
  });

  const handleSavePromptsFromModal = useLastCallback(async (updated: PromptsConfig) => {
    setIsSaving(true);
    setSaveSuccess(false);
    setErrorMessage('');
    try {
      const res = await fetch(`${BACKEND_URL}/ai-configs/prompts`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updated),
      });

      if (res.ok) {
        setInstitutionalPrompt(updated.institutional_prompt);
        setRagPrompt(updated.rag_context_prompt);
        setFormattingPrompt(updated.formatting_prompt);
        setIsPromptModalOpen(false);
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 4000);
      } else {
        setErrorMessage('Error al guardar los cambios en la base de datos.');
      }
    } catch {
      setErrorMessage('Error al comunicarse con la API de IA.');
    } finally {
      setIsSaving(false);
    }
  });

  const handleSaveBusinessHours = useLastCallback(async (payload: BusinessHoursData) => {
    setIsSavingSchedule(true);
    try {
      const res = await fetch(`${BACKEND_URL}/business-hours`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        const data = await res.json();
        setBusinessHours(data);
        setIsScheduleModalOpen(false);
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
      } else {
        setErrorMessage('Error al guardar horarios de atención.');
      }
    } catch {
      setErrorMessage('Error de red al actualizar horarios.');
    } finally {
      setIsSavingSchedule(false);
    }
  });

  const handleSaveAiConfig = useLastCallback(async (payload: AiConfigData) => {
    setIsSavingAi(true);
    try {
      const res = await fetch(`${BACKEND_URL}/ai-configs`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        const data = await res.json();
        setAiConfig(data.config || payload);
        setIsAiModalOpen(false);
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
      } else {
        setErrorMessage('Error al actualizar configuración del motor de IA.');
      }
    } catch {
      setErrorMessage('Error de red al actualizar IA.');
    } finally {
      setIsSavingAi(false);
    }
  });

  const handleOpenHelixModal = useLastCallback(() => {
    if (!canManageTeam) return;
    setIsHelixModalOpen(true);
  });

  const handleSaveHelixConfig = useLastCallback(async (payload: {
    base_url: string;
    tenant: string;
    token?: string;
  }) => {
    setIsSavingHelix(true);
    try {
      const res = await fetch(`${BACKEND_URL}/helix/config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        const data = await res.json();
        setHelixConfig(data.config);
        setIsHelixModalOpen(false);
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
      } else {
        setErrorMessage('Error al actualizar la configuración de HELIX.');
      }
    } catch {
      setErrorMessage('Error de red al actualizar HELIX.');
    } finally {
      setIsSavingHelix(false);
    }
  });

  return (
    <div className={styles.root}>
      <div className={styles.pageHeader}>
        <h2 className={styles.headerTitle}>
          Configuración Institucional del Sistema
        </h2>
        <p className={styles.headerSubtitle}>
          Parámetros operativos de la Facultad de Medicina UMSS, inteligencia artificial y equipo multi-operador.
        </p>
      </div>

      <div className={styles.settingsGrid}>
        {/* Horario de Atención */}
        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <h3 className={styles.sectionTitle}>Horarios de Atención de Oficina</h3>
            <button
              type="button"
              className={styles.btnEditHeader}
              onClick={() => setIsScheduleModalOpen(true)}
            >
              <Icon name="edit" />
              <span>Editar Horarios y Feriados</span>
            </button>
          </div>
          <div className={styles.row}>
            <span className={styles.label}>Días hábiles:</span>
            <span className={styles.value}>{formatWorkDays(businessHours?.days)}</span>
          </div>
          <div className={styles.row}>
            <span className={styles.label}>Franja horaria:</span>
            <span className={styles.value}>{formatWorkHours(businessHours?.days)}</span>
          </div>
          <div className={styles.row}>
            <span className={styles.label}>Zona horaria:</span>
            <span className={styles.value}>{businessHours?.timezone || 'América / La Paz (BOT)'}</span>
          </div>
          <div className={styles.row}>
            <span className={styles.label}>Modo Asistente:</span>
            <span className={styles.badge}>
              {businessHours?.assistant_mode === 'auto_reply'
                ? 'Auto-respuesta Fuera de Horario'
                : 'Supervisión Humana (Human-in-the-loop)'}
            </span>
          </div>
          <div className={styles.row}>
            <span className={styles.label}>Feriados y Efemérides:</span>
            <span className={styles.value}>
              {businessHours?.holidays
                ? `${businessHours.holidays.filter((h) => h.active).length} activos (incl. Urkupiña y 14 de Septiembre)`
                : '12 activos (Nacionales y Regionales Cochabamba)'}
            </span>
          </div>
        </section>

        {/* Motor de Inteligencia Artificial */}
        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <h3 className={styles.sectionTitle}>Motor de Inteligencia Artificial & RAG</h3>
            <button
              type="button"
              className={styles.btnEditHeader}
              onClick={() => setIsAiModalOpen(true)}
            >
              <Icon name="edit" />
              <span>Configurar Motor IA</span>
            </button>
          </div>
          <div className={styles.row}>
            <span className={styles.label}>Proveedor LLM:</span>
            <span className={styles.value}>
              {aiConfig?.name
                ? `${aiConfig.name} (${aiConfig.base_url || 'OpenAI Compatible'})`
                : 'DeepInfra API (OpenAI Compatible)'}
            </span>
          </div>
          <div className={styles.row}>
            <span className={styles.label}>Modelo de Inferencia:</span>
            <span className={styles.value}>{aiConfig?.model_name || 'DeepSeek-V4-Flash-0731'}</span>
          </div>
          <div className={styles.row}>
            <span className={styles.label}>Parámetros de Inferencia:</span>
            <span className={styles.value}>
              Temp:
              {' '}
              {aiConfig?.temperature !== undefined ? aiConfig.temperature.toFixed(2) : '0.20'}
              {' | Max Tokens: '}
              {aiConfig?.max_tokens || 350}
              {' | RAG: '}
              {aiConfig?.rag_limit || 2}
              {' chunks | Umbral: '}
              {aiConfig?.score_threshold !== undefined ? aiConfig.score_threshold.toFixed(2) : '0.45'}
            </span>
          </div>
          <div className={styles.row}>
            <span className={styles.label}>Modelo de Embeddings:</span>
            <span className={styles.value}>BAAI/bge-m3 (1024 dims)</span>
          </div>
          <div className={styles.row}>
            <span className={styles.label}>Base de Datos Vectorial:</span>
            <span className={styles.value}>Qdrant Vector DB (v1.11+)</span>
          </div>
          <div className={styles.row}>
            <span className={styles.label}>Estado del Servicio:</span>
            <span className={styles.badge}>✅ Conectado y Operativo</span>
          </div>
        </section>

        {/* Daemon Autónomo 24/7 (Telethon MTProto) */}
        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <h3 className={styles.sectionTitle}>Daemon Autónomo 24/7 (Telethon MTProto)</h3>
            <button
              type="button"
              className={styles.btnEditHeader}
              disabled={isTelethonChecking}
              onClick={() => setIsTelethonModalOpen(true)}
            >
              <Icon name="channel" />
              <span>
                {isTelethonChecking
                  ? 'Verificando...'
                  : (telethonStatus?.is_authorized ? 'Verificar Estado' : 'Vincular Teléfono')}
              </span>
            </button>
          </div>
          <div className={styles.row}>
            <span className={styles.label}>Teléfono Institucional:</span>
            <span className={styles.value}>
              {telethonStatus?.configured_phone || '+59157746549'}
              {' '}
              (Posgrado Medicina UMSS)
            </span>
          </div>
          <div className={styles.row}>
            <span className={styles.label}>Conexión Oficial Telegram:</span>
            <span className={styles.value}>
              {isTelethonChecking ? (
                <span className={styles.badgeMuted}>⏳ Comprobando conexión...</span>
              ) : telethonStatus?.is_connected ? (
                <span className={styles.badge}>
                  {`🟢 Conectado (DC ${telethonStatus.dc_id || 1} - MTProto)`}
                </span>
              ) : telethonStatus?.has_db_session || telethonStatus?.status === 'reconnecting' ? (
                <span className={styles.badgeWarning}>🟡 Reconectando daemon (Sesión en MariaDB)...</span>
              ) : (
                <span className={styles.badgeDanger}>🔴 Desconectado</span>
              )}
            </span>
          </div>
          <div className={styles.row}>
            <span className={styles.label}>Sesión Criptográfica:</span>
            <span className={styles.value}>
              {isTelethonChecking ? (
                <span className={styles.badgeMuted}>⏳ Verificando credenciales...</span>
              ) : telethonStatus?.is_authorized ? (
                <span className={styles.badge}>
                  🟢 Activa •
                  {' '}
                  {telethonStatus.user?.first_name || 'Autorizado'}
                  {telethonStatus.user?.username ? ` (@${telethonStatus.user.username})` : ''}
                </span>
              ) : telethonStatus?.has_db_session ? (
                <span className={styles.badge}>
                  🟢 Respaldada en MariaDB (Iniciando daemon)
                </span>
              ) : (
                <span className={styles.badgeWarning}>
                  🟡 Pendiente de Vinculación (Requiere Código 5 Dígitos)
                </span>
              )}
            </span>
          </div>
          <div className={styles.row}>
            <span className={styles.label}>Almacenamiento de Sesión:</span>
            <span className={styles.value}>
              {isTelethonChecking ? (
                <span className={styles.badgeMuted}>⏳ Consultando...</span>
              ) : (
                <span className={styles.badge}>
                  🗄️ MariaDB (telegram_sessions) + Respaldo Local
                </span>
              )}
            </span>
          </div>
          <div className={styles.row}>
            <span className={styles.label}>Debounce Anti-Spam:</span>
            <span className={styles.value}>4.0 segundos (Consolidación de ráfagas)</span>
          </div>
          <div className={styles.row}>
            <span className={styles.label}>Ventana de Auto-Respuesta:</span>
            <span className={styles.value}>Noches (&lt; 08:00 o &gt; 16:00), Sábados, Domingos y Feriados</span>
          </div>
        </section>

        {/* Interconexión HELIX (Gestión Académica & Expedientes) */}
        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <h3 className={styles.sectionTitle}>Interconexión HELIX (Gestión Académica & Expedientes)</h3>
            {canManageTeam && (
              <button
                type="button"
                className={styles.btnEditHeader}
                onClick={handleOpenHelixModal}
              >
                <Icon name="link" />
                <span>Configurar HELIX</span>
              </button>
            )}
          </div>
          <div className={styles.row}>
            <span className={styles.label}>URL Base API:</span>
            <span className={styles.value}>{helixConfig?.base_url || 'https://helix.med.umss.edu.bo/api/v1'}</span>
          </div>
          <div className={styles.row}>
            <span className={styles.label}>Tenant Activo:</span>
            <span className={styles.value}>{helixConfig?.tenant || 'medicina'}</span>
          </div>
          <div className={styles.row}>
            <span className={styles.label}>Token de Seguridad M2M:</span>
            <span className={styles.value}>
              {helixConfig?.has_token ? (
                <code>{helixConfig.token_masked}</code>
              ) : (
                <span className={styles.textMuted}>No configurado</span>
              )}
            </span>
          </div>
          <div className={styles.row}>
            <span className={styles.label}>Estado de Conexión:</span>
            <span className={styles.value}>
              {helixConfig?.last_status === 'connected' ? (
                <span className={styles.badge}>
                  🟢 Conectado y Operativo
                  {helixConfig.last_latency_ms !== undefined ? ` (${helixConfig.last_latency_ms} ms)` : ''}
                </span>
              ) : helixConfig?.last_status === 'error' ? (
                <span className={styles.badgeDanger}>
                  🔴 Error de Conexión
                </span>
              ) : (
                <span className={styles.badgeWarning}>
                  🟡 Pendiente de Verificación
                </span>
              )}
            </span>
          </div>
          {helixConfig?.last_error && (
            <div className={styles.row}>
              <span className={styles.label}>Último Diagnóstico:</span>
              <span className={styles.valueDanger}>{helixConfig.last_error}</span>
            </div>
          )}
        </section>

        {/* Gestión de Prompts del Sistema (IA) */}
        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <div>
              <h3 className={styles.sectionTitle}>Directivas y Prompts Centrales del Asistente IA</h3>
              <p className={styles.promptSubtitle}>
                Edita el comportamiento, reglas de oro y formato de respuesta sin necesidad de regenerar código.
              </p>
            </div>
            <span className={styles.badge}>Modificable en Caliente</span>
          </div>

          {saveSuccess && (
            <div className={styles.alertSuccess}>
              ✅ Configuración guardada exitosamente. Se aplicará a todas las consultas entrantes.
            </div>
          )}

          {errorMessage && (
            <div className={styles.alertError}>
              ⚠️
              {' '}
              {errorMessage}
            </div>
          )}

          {isLoading ? (
            <div className={styles.loadingText}>
              Cargando directivas de inteligencia artificial...
            </div>
          ) : (
            <>
              <div className={styles.promptsList}>
                {/* Tarjeta Prompt 1 */}
                <div
                  className={styles.promptCardItem}
                  onClick={() => handleOpenPromptModal('institutional')}
                >
                  <div className={styles.promptCardLeft}>
                    <div className={styles.promptNumberBadge}>1</div>
                    <div className={styles.promptCardContent}>
                      <div className={styles.promptCardHeader}>
                        <span className={styles.promptCardTitle}>
                          Prompt Institucional (Identidad, Reglas Críticas y Canales)
                        </span>
                        <span className={styles.promptCountBadge}>
                          {institutionalPrompt.length}
                          {' '}
                          caracteres
                        </span>
                      </div>
                      <div className={styles.promptPreview}>
                        {institutionalPrompt.replace(/\n+/g, ' ').slice(0, 140)}
                        ...
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    className={styles.btnEditPrompt}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenPromptModal('institutional');
                    }}
                  >
                    <Icon name="edit" />
                    <span>Editar en Extenso</span>
                  </button>
                </div>

                {/* Tarjeta Prompt 2 */}
                <div
                  className={styles.promptCardItem}
                  onClick={() => handleOpenPromptModal('rag')}
                >
                  <div className={styles.promptCardLeft}>
                    <div className={styles.promptNumberBadge}>2</div>
                    <div className={styles.promptCardContent}>
                      <div className={styles.promptCardHeader}>
                        <span className={styles.promptCardTitle}>
                          Directivas RAG y Anti-Alucinación (Grounding Fáctico)
                        </span>
                        <span className={styles.promptCountBadge}>
                          {ragPrompt.length}
                          {' '}
                          caracteres
                        </span>
                      </div>
                      <div className={styles.promptPreview}>
                        {ragPrompt.replace(/\n+/g, ' ').slice(0, 140)}
                        ...
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    className={styles.btnEditPrompt}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenPromptModal('rag');
                    }}
                  >
                    <Icon name="edit" />
                    <span>Editar en Extenso</span>
                  </button>
                </div>

                {/* Tarjeta Prompt 3 */}
                <div
                  className={styles.promptCardItem}
                  onClick={() => handleOpenPromptModal('formatting')}
                >
                  <div className={styles.promptCardLeft}>
                    <div className={styles.promptNumberBadge}>3</div>
                    <div className={styles.promptCardContent}>
                      <div className={styles.promptCardHeader}>
                        <span className={styles.promptCardTitle}>
                          Formato y Estilo de Respuesta para Telegram
                        </span>
                        <span className={styles.promptCountBadge}>
                          {formattingPrompt.length}
                          {' '}
                          caracteres
                        </span>
                      </div>
                      <div className={styles.promptPreview}>
                        {formattingPrompt.replace(/\n+/g, ' ').slice(0, 140)}
                        ...
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    className={styles.btnEditPrompt}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenPromptModal('formatting');
                    }}
                  >
                    <Icon name="edit" />
                    <span>Editar en Extenso</span>
                  </button>
                </div>
              </div>

              <div className={styles.actionsRow}>
                <button
                  type="button"
                  className={styles.btnSecondary}
                  onClick={handleResetDefaults}
                  disabled={isSaving}
                >
                  Restablecer los 3 Prompts por Defecto
                </button>
              </div>
            </>
          )}
        </section>

        {/* Equipo Multi-Operador */}
        {canManageTeam && (
          <section className={styles.section}>
            <div className={styles.sectionHeader}>
              <h3 className={styles.sectionTitle}>
                Equipo Multi-Operador
                {' '}
                (
                {operators.length}
                {' '}
                puestos)
              </h3>
              <button
                type="button"
                className={styles.btnEditHeader}
                onClick={handleOpenOperatorsModal}
              >
                <Icon name="group" />
                <span>Gestionar Equipo</span>
              </button>
            </div>
            <div className={styles.operatorsList}>
              {operators.map((op) => (
                <div key={op.id} className={styles.operatorCard}>
                  <div className={styles.operatorAvatar}>
                    {op.name.charAt(0)}
                  </div>
                  <div className={styles.operatorInfo}>
                    <span className={styles.operatorName}>{op.name}</span>
                    <span className={styles.operatorRole}>{op.role}</span>
                  </div>
                </div>
              ))}
            </div>
            <div className={styles.operatorsBadgeRow}>
              <span className={styles.badge}>Sesión Concurrente MTProto</span>
            </div>
          </section>
        )}
      </div>

      {/* Modales */}
      <ScheduleEditorModal
        isOpen={isScheduleModalOpen}
        initialData={businessHours}
        isSaving={isSavingSchedule}
        onClose={() => setIsScheduleModalOpen(false)}
        onSave={handleSaveBusinessHours}
      />

      <AiConfigModal
        isOpen={isAiModalOpen}
        initialConfig={aiConfig}
        isSaving={isSavingAi}
        onClose={() => setIsAiModalOpen(false)}
        onSave={handleSaveAiConfig}
      />

      {canManageTeam && (
        <HelixConfigModal
          isOpen={isHelixModalOpen}
          initialConfig={helixConfig}
          isSaving={isSavingHelix}
          onClose={() => setIsHelixModalOpen(false)}
          onSave={handleSaveHelixConfig}
        />
      )}

      <PromptEditorModal
        isOpen={isPromptModalOpen}
        initialPromptKey={activePromptKey}
        prompts={{
          institutional_prompt: institutionalPrompt,
          rag_context_prompt: ragPrompt,
          formatting_prompt: formattingPrompt,
        }}
        isSaving={isSaving}
        onClose={() => setIsPromptModalOpen(false)}
        onSave={handleSavePromptsFromModal}
        onResetPrompt={handleResetDefaults}
      />

      {canManageTeam && (
        <OperatorsModal
          isOpen={isOperatorsModalOpen}
          operators={operators}
          onClose={() => setIsOperatorsModalOpen(false)}
          onChange={handleOperatorsChange}
        />
      )}

      <TelethonAuthModal
        isOpen={isTelethonModalOpen}
        configuredPhone={telethonStatus?.configured_phone || '+59157746549'}
        onClose={() => setIsTelethonModalOpen(false)}
        onSuccess={loadData}
      />
    </div>
  );
};

export default memo(SystemSettingsView);
